import { TellUserActionParams, ControlLightActionParams, ControlThermostatActionParams, ControlThermostatActionParams_TemperatureUnit, ExactTimeConditionParams, BoundedTimeConditionParams, EventConditionParams, EmailFromSenderConditionParams, AutomationParams, RecurrenceParams, EventStatus, DateConditionParams, ActivityStatusConditionParams } from "tsproto/aware_proto/proto/Modules";
import { Any } from "tsproto/google/protobuf/any";
import spacetime from "spacetime";
import NLP from "../NLP";
import DateUtils from "../DateUtils";
import ProtoUtils from "../ProtoUtils";
import { Timestamp } from "tsproto/google/protobuf/timestamp";
import { nanoid } from "nanoid";
export default class Automations {
    static unpackAction(anyProto) {
        const name = getActionName(anyProto);
        const matcher = Actions[name];
        if (!matcher) {
            return undefined;
        }
        return Any.unpack(anyProto, matcher.paramsType());
    }
    static unpackCondition(anyProto) {
        const name = getConditionName(anyProto);
        const matcher = Conditions[name];
        if (!matcher) {
            return undefined;
        }
        return Any.unpack(anyProto, matcher.paramsType());
    }
}
class TellUserActionMatcher {
    paramsType() { return TellUserActionParams; }
    match(context) {
        const doc = context.doc;
        const intent = context.intent;
        const env = context.env;
        const exprs = [
            "(tell|notify|inform|remind|warn) [(me|#UserName|#Person)] [to #Verb *]?",
            "let [(me|#UserName|#Person)] know [.*]?"
        ];
        for (const expr of exprs) {
            const reqExpr = requestExpr(expr);
            let match = doc.match(reqExpr);
            if (match.text()) {
                // Shrink the full text to exclude conditions
                let reducedText = match.text();
                const prepExprs = ["#Preposition", IfExpr, RecurringExpr];
                for (const prepExpr of prepExprs) {
                    const matchPrep = doc.match(prepExpr);
                    if (matchPrep.text()) {
                        const array = doc.splitBefore(matchPrep).out("array");
                        if (array[0].length < reducedText.length) {
                            reducedText = array[0];
                        }
                    }
                }
                // Match again on the reduced text
                match = NLP.doc(reducedText).match(NLP.replaceWildcards(reqExpr));
                const matchedText = match.text();
                const group0Text = match.groups("0").text();
                const group1Text = match.groups("1")?.text() || "";
                const vp = group1Text;
                const params = TellUserActionParams.create({
                    originUserUid: intent?.speakerUid,
                    recipientUserUid: group0Text === "me" ? intent?.speakerUid :
                        env?.getUserByDisplayName(group0Text)?.uid || "",
                    vp: vp,
                    nlText: matchedText
                });
                return [true, params];
            }
        }
        return [false];
    }
    describe(params, env) {
        let userName;
        if (params.originUserUid === params.recipientUserUid) {
            userName = "me";
        }
        else {
            const user = env.getUserByUid(params.recipientUserUid);
            userName = user?.getDisplayName();
        }
        userName = userName || "?";
        return `tell ${userName}`;
    }
}
class ControlLightActionMatcher {
    paramsType() { return ControlLightActionParams; }
    match(context) {
        const doc = context.doc;
        const expr = requestExpr("turn [(on|off)] the [*]? light");
        const match = doc.match(expr);
        if (match.text()) {
            const group0Text = match.groups("0").text();
            const group1Text = match.groups("1").text();
            const params = ControlLightActionParams.create({
                on: group0Text === "on",
                lightName: group1Text,
                nlText: match.text()
            });
            return [true, params];
        }
        return [false];
    }
    describe(params, env) {
        return `turn ${params.on ? "on" : "off"} the ${params.lightName ? params.lightName + " " : ""}light`;
    }
}
class ControlThermostatActionMatcher {
    paramsType() { return ControlThermostatActionParams; }
    match(context) {
        const doc = context.doc;
        const expr = requestExpr("set the (temperature|thermostat) to [#Value] degrees? [(fahrenheight|celsius)]?");
        const match = doc.match(expr);
        if (match.text()) {
            const group0Text = match.groups("0").text();
            const group1Text = match.groups("1").text();
            let value;
            try {
                const numDoc = NLP.doc(group0Text).numbers().toNumber();
                value = Number(numDoc.text());
            }
            catch (e) {
                return [false];
            }
            let unit = ControlThermostatActionParams_TemperatureUnit.UnknownTemperatureUnit;
            if (group1Text) {
                unit = group1Text === "fahrenheit" ? ControlThermostatActionParams_TemperatureUnit.Fahrenheit :
                    ControlThermostatActionParams_TemperatureUnit.Celsius;
            }
            else {
                if (value > 40) {
                    unit = ControlThermostatActionParams_TemperatureUnit.Fahrenheit;
                }
                else {
                    unit = ControlThermostatActionParams_TemperatureUnit.Celsius;
                }
            }
            const params = ControlThermostatActionParams.create({
                degrees: value,
                unit: unit,
                nlText: match.text()
            });
            return [true, params];
        }
        return [false];
    }
    describe(params, env) {
        const unitStr = params.unit === ControlThermostatActionParams_TemperatureUnit.Celsius ? "C" : "F";
        return `set the temperature to ${params.degrees}˚${unitStr}`;
    }
}
class ExactTimeConditionMatcher {
    paramsType() { return ExactTimeConditionParams; }
    match(context) {
        const doc = context.doc;
        const intent = context.intent;
        const env = context.env;
        const [matched, timestamp, timeText, dateText, matchedText, timePhrase, datePhrase] = matchTimeAndDate("at [#Time]", intent, doc, env);
        if (matched) {
            // console.log(`Time: ${timePhrase} | Date: ${datePhrase}`);
            const params = ExactTimeConditionParams.create({
                time: timestamp,
                timeText: timeText,
                dateText: dateText,
                nlText: matchedText
            });
            return [true, params];
        }
        return [false];
    }
    describe(params, env) {
        let text = `at ${params.timeText}`;
        if (params.dateText) {
            text += ` on ${params.dateText}`;
        }
        return text;
    }
}
class BoundedTimeConditionMatcher {
    paramsType() { return BoundedTimeConditionParams; }
    match(context) {
        const doc = context.doc;
        const intent = context.intent;
        const env = context.env;
        const [matched, timestamp, timeText, dateText, matchedText, timePhrase, datePhrase] = matchTimeAndDate("(until|before) [#Time]", intent, doc, env);
        if (matched) {
            // console.log(`Time: ${timePhrase} | Date: ${datePhrase}`);
            const params = BoundedTimeConditionParams.create({
                deadline: timestamp,
                timeText: timeText,
                dateText: dateText,
                nlText: matchedText
            });
            return [true, params];
        }
        const durations = doc.durations().json();
        const match = doc.match("(for|in|over) the next [(minute|hour|day|week|month|year)?]");
        if (match.text()) {
            const user = env?.getUserByUid(intent.speakerUid);
            const tz = user?.timezone() || DateUtils.localTimezone();
            const stNow = spacetime.now(tz);
            if (durations.length > 0) {
                const durationText = durations[0].text;
                const matchedText = `${match.text()} ${durationText}`;
                const duration = NLP.getDuration(durations[0]);
                const durationSecs = NLP.getDurationSeconds(duration);
                const stTime = stNow.add(durationSecs, "seconds");
                const timestamp = ProtoUtils.toTimestamp(stTime);
                const params = BoundedTimeConditionParams.create({
                    deadline: timestamp,
                    timeText: stTime.format("time"),
                    nlText: matchedText
                });
                return [true, params];
            }
            if (match.groups.length > 0) {
                const group0Text = match.groups("0").text();
                const stTime = stNow.add(1, group0Text);
                const timestamp = ProtoUtils.toTimestamp(stTime);
                const params = BoundedTimeConditionParams.create({
                    deadline: timestamp,
                    timeText: stTime.format("time"),
                    nlText: match.text()
                });
                return [true, params];
            }
        }
        return [false];
    }
    describe(params, env) {
        let text = `until ${params.timeText}`;
        if (params.dateText) {
            text += ` on ${params.dateText}`;
        }
        return text;
    }
}
class DateConditionMatcher {
    paramsType() { return DateConditionParams; }
    match(context) {
        const doc = context.doc;
        const intent = context.intent;
        const env = context.env;
        // Only match when no time is specified
        if (doc.times().length > 0) {
            return [false];
        }
        // Don't duplicate matches with bounded time
        const [matched] = Conditions.BoundedTime.match(context);
        if (matched) {
            return [false];
        }
        const exprs = ["on [#Date]", "[#Date]"];
        for (const expr of exprs) {
            const match = doc.match(expr);
            if (match.text()) {
                const group0Text = match.groups("0").text();
                const dateMatch = NLP.doc(group0Text).dates().json()[0];
                const user = env?.getUserByUid(intent.speakerUid);
                const tz = user?.timezone() || DateUtils.localTimezone();
                const stStart = spacetime(dateMatch.dates.start, tz);
                const stEnd = spacetime(dateMatch.dates.end, tz);
                const params = DateConditionParams.create({
                    start: Timestamp.fromDate(stStart.toNativeDate()),
                    end: Timestamp.fromDate(stEnd.toNativeDate()),
                    dateText: stStart.format("YYYY-MM-DD"),
                    nlText: match.text()
                });
                return [true, params];
            }
        }
        return [false];
    }
    describe(params, env) {
        return `on ${params.dateText}`;
    }
}
class EventConditionMatcher {
    paramsType() { return EventConditionParams; }
    match(context) {
        const doc = context.doc;
        const intent = context.intent;
        const env = context.env;
        let isRecurring;
        if (context.automation) {
            isRecurring = context.automation.recurring;
        }
        else {
            const [matched,] = Recurrence.match(context);
            isRecurring = matched;
        }
        const exprs = [
            `${IfExpr} (I go|I'm going) to [.*]`,
            `${IfExpr} I visit [.*]`,
            `${IfExpr} (I get|I'm getting) ready (for|to go to|to visit) [.*]`,
            NLP.replaceWildcards("to [.*]", ["#Verb"]),
            `${IfExpr} I? (go to)? my? [.*]`,
        ];
        for (const expr of exprs) {
            const match = doc.match(expr);
            if (match.text()) {
                const group0Text = match.groups("0").text();
                let inputText = group0Text;
                // Get just the last "to" segment if there are multiple
                const tos = match.text().split("to");
                if (tos.length > 2) {
                    inputText = tos.at(-1).trim();
                }
                const user = env?.getUserByUid(intent?.speakerUid);
                const userSchedule = user?.userState?.userSchedule;
                // Check for both place and event name
                const eventAtPlaceDoc = NLP.doc(inputText, NLP.getLexicon(intent?.speakerUid, env));
                const eventAtPlaceMatch = eventAtPlaceDoc.match("[.*] at [.*]");
                if (eventAtPlaceMatch.text()) {
                    const eventText = eventAtPlaceMatch.groups("0").text();
                    const placeText = eventAtPlaceMatch.groups("1").text();
                    // Check places first
                    const [places, matches] = NLP.matchPlaces(placeText, user);
                    if (places.length > 0) {
                        const place = places[0];
                        const nlText = match.text();
                        const params = EventConditionParams.create({
                            placeUid: place.placeUid,
                            placeDisplayName: matches[0].placeName,
                            placeText: placeText,
                            nlText: nlText
                        });
                        // Check event names
                        const mainEvents = NLP.matchEvents(inputText, userSchedule, !isRecurring);
                        const mainEvent = mainEvents?.[0];
                        Object.assign(params, {
                            eventUid: mainEvent?.event.eventUid || "",
                            eventName: mainEvent?.event.eventName || "",
                            eventText: eventText,
                        });
                        return [true, params];
                    }
                }
                // If both weren't matched, check places and events individually
                // Check place names
                const [places, matches] = NLP.matchPlaces(inputText, user);
                if (places.length > 0) {
                    const place = places[0];
                    const nlText = match.text();
                    const params = EventConditionParams.create({
                        placeUid: place.placeUid,
                        placeDisplayName: matches[0].placeName,
                        placeText: inputText,
                        nlText: nlText
                    });
                    return [true, params];
                }
                // Check event names
                const mainEvents = NLP.matchEvents(inputText, userSchedule, !isRecurring);
                const mainEvent = mainEvents?.[0];
                const nlText = match.text();
                const params = EventConditionParams.create({
                    eventUid: mainEvent?.event.eventUid || "",
                    eventName: mainEvent?.event.eventName || "",
                    eventText: group0Text,
                    nlText: nlText
                });
                if (isRecurring || !isRecurring && params.eventUid) {
                    return [true, params];
                }
            }
        }
        return [false];
    }
    describe(params, env) {
        if (params.eventName && params.placeDisplayName) {
            return `when I go to ${params.eventName} at ${params.placeDisplayName}`;
        }
        if (params.placeDisplayName && !params.eventName) {
            return `when I go to ${params.placeDisplayName}`;
        }
        if (params.eventName && !params.placeDisplayName) {
            return `to ${params.eventName}`;
        }
        return "";
    }
}
class EmailFromSenderConditionMatcher {
    paramsType() { return EmailFromSenderConditionParams; }
    match(context) {
        const doc = context.doc;
        const senderExpr = "(#Email|#Url|#Noun+)";
        const exprs = [
            `${IfExpr} (I get|I receive|you see) (an|any)? (email|emails) from [${senderExpr}]`,
            `${IfExpr} (I am|I'm|I get) emailed by [${senderExpr}]`,
            `${IfExpr} [${senderExpr}] emails me`,
            `${IfExpr} [${senderExpr}] sends me? (an|any)? (email|emails)`,
        ];
        for (const expr of exprs) {
            const match = doc.match(expr);
            if (match.text()) {
                const group0Text = match.groups("0").text();
                const params = EmailFromSenderConditionParams.create({
                    sender: group0Text,
                    nlText: match.text()
                });
                return [true, params];
            }
        }
        return [false];
    }
    describe(params, env) {
        return `if ${params.sender} emails me`;
    }
}
class ActivityStatusConditionMatcher {
    paramsType() { return ActivityStatusConditionParams; }
    match(context) {
        const doc = context.doc;
        const startExprs = ["start", "starting", "begin", "beginning"];
        const endExprs = ["finish", "finishing", "end", "ending", "stop", "stopping", "done with", "done",
            "finished with", "finished"];
        const duringExprs = ["while", "during"];
        const eventStatusExpr = optionsExpr([...startExprs, ...endExprs, ...duringExprs]);
        const exerciseExprs = ["a workout session", "my workout session", "a workout", "my workout",
            "working out", "to work out", "work out", "to exercise", "exercise", "exercising",
            "my exercise session", "an exercise session", "my exercise"];
        const activityExpr = optionsExpr(exerciseExprs);
        const exprs = [
            `${IfExpr} (you see me)? (at the)? (I am|I'm|I have|I've|I)? [${eventStatusExpr}]? [${activityExpr}]`,
            `(after|once) (you see me)? (at the)? (I am|I'm|I have|I've|I)? [${eventStatusExpr}]? [${activityExpr}]`,
            `[${optionsExpr(duringExprs)}] (I am|I'm|I)? (in|having)? [${activityExpr}]`,
        ];
        for (const expr of exprs) {
            const match = doc.match(expr);
            if (match.text()) {
                const params = ActivityStatusConditionParams.create();
                const group0Text = match.groups("0").text();
                if (startExprs.includes(group0Text)) {
                    params.eventStatus = EventStatus.Start;
                }
                else if (endExprs.includes(group0Text)) {
                    params.eventStatus = EventStatus.End;
                }
                else {
                    params.eventStatus = EventStatus.During;
                }
                const group1Text = match.groups("1").text();
                if (exerciseExprs.includes(group1Text)) {
                    params.activityCategory = "User.Workout";
                }
                params.nlText = match.text();
                return [true, params];
            }
        }
        return [false];
    }
    describe(params, env) {
        let eventStr = "";
        if (params.eventStatus === EventStatus.Start) {
            eventStr = "when I start";
        }
        else if (params.eventStatus === EventStatus.End) {
            eventStr = "when I finish";
        }
        else {
            eventStr = "while I'm";
        }
        let activityStr = "";
        if (params.activityCategory === "User.Workout") {
            activityStr = "working out";
        }
        return `${eventStr} ${activityStr}`;
    }
}
class AutomationMatcher {
    paramsType() { return AutomationParams; }
    match(context) {
        const doc = context.doc;
        const intent = context.intent;
        const env = context.env;
        const matchedIndices = new Array(doc.text().length).fill(0);
        let automation = context.automation;
        if (!automation) {
            automation = AutomationParams.create({
                ownerUid: intent?.speakerUid || "",
                timestampCreated: Timestamp.now()
            });
        }
        const actions = Object.values(Actions);
        let matchedAction, matchedActionParams;
        for (const action of actions) {
            const [matched, params] = action.match(context);
            if (matched) {
                matchedAction = action;
                matchedActionParams = params;
                automation.action = Any.pack(params, action.paramsType());
                const index = doc.text().indexOf(params.nlText);
                if (index > -1) {
                    matchedIndices.fill(1, index, index + params.nlText.length);
                }
                // Set the UID based on the action name for readability
                const actionName = getActionName(automation.action);
                const date = Timestamp.toDate(automation.timestampCreated);
                automation.automationUid = `${actionName}:${date.toISOString()}:${nanoid(8)}`;
                break;
            }
        }
        if (!automation.action) {
            // Need to match an action for it to be an automation
            return [false];
        }
        context.automation = automation;
        // Match recurrence
        const match = doc.match(RecurringExpr);
        if (match.text()) {
            automation.recurring = true;
            const index = doc.text().indexOf(match.text());
            if (index > -1) {
                matchedIndices.fill(3, index, index + match.text().length);
            }
        }
        const recurrence = Recurrence;
        const [matched, params] = recurrence.match(context);
        if (matched) {
            automation.recurrence = params;
            const index = doc.text().indexOf(params.nlText);
            if (index > -1) {
                matchedIndices.fill(3, index, index + match.text().length);
            }
        }
        // Match conditions
        const conditions = Object.values(Conditions);
        for (const condition of conditions) {
            const [matched, params] = condition.match(context);
            if (matched) {
                const proto = Any.pack(params, condition.paramsType());
                automation.conditions.push(proto);
                const index = doc.text().indexOf(params.nlText);
                if (index > -1) {
                    matchedIndices.fill(2, index, index + params.nlText.length);
                }
            }
        }
        if (automation.conditions.length === 0) {
            // Need to match at least one condition for it to be an automation
            return [false];
        }
        // Return the matched text accumulated from the action and conditions.
        automation.nlText = maskString(doc.text(), matchedIndices, v => v !== 0);
        // Shrink the action text based on condition / recurrence matching
        const actionText = maskString(doc.text(), matchedIndices, v => v === 1);
        if (actionText !== matchedActionParams.nlText) {
            const reducedDoc = NLP.doc(actionText);
            const context = { intent: intent, doc: reducedDoc, env: env };
            const [matched, params] = matchedAction.match(context);
            if (matched) {
                automation.action = Any.pack(params, matchedAction.paramsType());
            }
        }
        return [true, automation];
    }
    describe(params, env) {
        return params.nlText;
    }
}
class RecurrenceMatcher {
    paramsType() { return RecurrenceParams; }
    match(context) {
        const doc = context.doc;
        const dailyExprs = [
            "of? (every|each) (day|#TimeOfDay)",
            "once? per? every (day|#TimeOfDay)",
            "(daily|nightly)"
        ];
        for (const dailyExpr of dailyExprs) {
            const match = doc.match(dailyExpr);
            if (match.text()) {
                const matchedText = match.text();
                const params = RecurrenceParams.create({
                    value: 1,
                    timeUnit: "day",
                    nlText: matchedText
                });
                return [true, params];
            }
        }
        const durationExprs = [
            "of? every #Value+ #Duration+",
            "once? per? every #Value+ #Duration+"
        ];
        for (const durationExpr of durationExprs) {
            const match = doc.match(durationExpr);
            if (match.text()) {
                const matchedText = match.text();
                const duration = NLP.getDuration(doc.durations().json()[0]);
                if (Object.keys(duration).length > 0) {
                    if (Object.keys(duration).length > 1) {
                        console.warn(`Multiple duration units not currently handled! Text was: ${matchedText}`);
                    }
                    const params = RecurrenceParams.create({
                        value: Object.values(duration)[0],
                        timeUnit: Object.keys(duration)[0],
                        nlText: matchedText
                    });
                    return [true, params];
                }
            }
        }
        const timeUnitExprs = [
            "of? (each|every) [#TimeUnit]",
            "once? (a|an|per|each|every) [#TimeUnit]"
        ];
        for (const timeUnitExpr of timeUnitExprs) {
            const match = doc.match(timeUnitExpr);
            if (match.text()) {
                const matchedText = match.text();
                const group0Text = match.groups("0").text();
                const params = RecurrenceParams.create({
                    value: 1,
                    timeUnit: group0Text,
                    nlText: matchedText
                });
                return [true, params];
            }
        }
        return [false];
    }
    describe(params, env) {
        return params.nlText;
    }
}
const IfExpr = "(if|when|whenever|before|prior to|next time|the next time|every time|anytime|any time)* that?";
const RecurringExpr = "(whenever|daily|nightly|always|anytime|any time|every #TimeOfDay|every time|every)";
function requestExpr(vp) {
    return `please? (can you|could you|would you)? please? ${vp}`;
}
function optionsExpr(options) {
    return `(${options.join("|")})`;
}
function getActionName(anyProto) {
    return Object.keys(Actions).find(key => anyProto.typeUrl.endsWith(Actions[key].paramsType().typeName));
}
function getConditionName(anyProto) {
    return Object.keys(Conditions).find(key => anyProto.typeUrl.endsWith(Conditions[key].paramsType().typeName));
}
function matchTimeAndDate(expr, intent, doc, env) {
    const match = doc.match(expr);
    if (match.text()) {
        let matchedText = match.text();
        let timePhrase = match.text(), datePhrase = "";
        const group0Text = match.groups("0").text();
        const times = NLP.doc(group0Text).times().json();
        if (times.length > 0) {
            let timeText = times[0].time.time;
            const user = env?.getUserByUid(intent.speakerUid);
            const tz = user?.timezone() || DateUtils.localTimezone();
            // Check for dates
            const dates = doc.dates().json();
            let stEnd;
            const stNow = spacetime.now(tz);
            let dateWithTimeText;
            if (dates.length > 0) {
                dateWithTimeText = dates[0].text;
                const endStr = dates[0].dates.end;
                stEnd = spacetime(endStr, tz).startOf("day");
                if (dateWithTimeText?.length > matchedText.length) {
                    matchedText = dateWithTimeText;
                }
                // Check if a day/date was explicitly mentioned
                const terms = dates[0].terms;
                const timeTerms = [], dateTerms = [];
                for (const term of terms) {
                    if (term.tags.includes("Time") || term.text.toLowerCase() === "until") {
                        timeTerms.push(term);
                    }
                    else if (!term.tags.includes("Determiner")) {
                        dateTerms.push(term);
                    }
                }
                timePhrase = timeTerms.map(v => v.text).join(" ");
                datePhrase = dateTerms.map(v => v.text).join(" ");
            }
            else {
                stEnd = stNow;
            }
            // Extra check for am/pm if it wasn't explicitly mentioned
            if (dateWithTimeText && !group0Text.toLowerCase().match(/[ap][m]/)) {
                const matchTimeOfDay = NLP.doc(dateWithTimeText).match("#TimeOfDay");
                if (matchTimeOfDay.text()) {
                    if (matchTimeOfDay.text() === "morning") {
                        timeText = timeText.replace("pm", "am");
                    }
                    else {
                        timeText = timeText.replace("am", "pm");
                    }
                }
            }
            let stTime = stEnd.time(timeText, true);
            if (stTime.isSame(stNow, "day") && stTime.isBefore(stNow)) {
                stTime = stTime.add(1, "day");
            }
            const timestamp = ProtoUtils.toTimestamp(stTime);
            const dateText = datePhrase ? stTime.format("YYYY-MM-DD") : "";
            return [true, timestamp, timeText, dateText, matchedText, timePhrase, datePhrase];
        }
    }
    return [false];
}
function maskString(input, indices, fn) {
    const maskedString = Array.from(input);
    for (let i = 0; i < indices.length; ++i) {
        if (maskedString[i] === " ")
            continue; // ignore spaces
        if (!fn(indices[i])) {
            maskedString[i] = "*";
        }
    }
    let result = maskedString.join(""); // created masked string
    result = result.replace(/\*/g, ""); // remove masked characters
    result = result.replace(/  +/g, " "); // replace multiple spaces with single spaces
    result = result.trim();
    return result;
}
const Actions = {
    TellUser: new TellUserActionMatcher(),
    ControlLight: new ControlLightActionMatcher(),
    ControlThermostat: new ControlThermostatActionMatcher()
};
const Conditions = {
    ExactTime: new ExactTimeConditionMatcher(),
    BoundedTime: new BoundedTimeConditionMatcher(),
    Date: new DateConditionMatcher(),
    Event: new EventConditionMatcher(),
    EmailFromSender: new EmailFromSenderConditionMatcher(),
    ActivityStatus: new ActivityStatusConditionMatcher()
};
const Automation = new AutomationMatcher();
const Recurrence = new RecurrenceMatcher();
export { Actions, Conditions, Automation, Recurrence, requestExpr, getActionName, getConditionName };
